import { describeMutationError } from "@api/mutationErrors";
import type { Room } from "@rooms/roomsClient";
import {
  Button,
  Dialog,
  FieldError,
  Input,
  SegmentedControl,
  Select,
} from "@scribenote/brian-design-system";
import { useState } from "react";

import {
  DISPLAY_KIND_LABEL,
  type Display,
  type DisplayKind,
} from "./displaysClient";
import { useCreateDisplay } from "./displaysQueries";
import { PairDisplayDialog } from "./PairDisplayButton";

const KIND_OPTIONS = (Object.keys(DISPLAY_KIND_LABEL) as DisplayKind[]).map(
  (value) => ({ value, label: DISPLAY_KIND_LABEL[value] }),
);

const NO_ROOM = "none";

// A clinic board shows every room, so it is never tied to one.
const ROOM_FIELD: Record<DisplayKind, "required" | "optional" | "none"> = {
  exam_room: "required",
  clinic_board: "none",
  treatment_board: "optional",
};

export const AddDisplayButton = ({
  rooms,
  sessionKey,
}: {
  rooms: Room[];
  sessionKey: string;
}) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<DisplayKind>("exam_room");
  const [roomId, setRoomId] = useState<string | null>(null);
  const [added, setAdded] = useState<Display | null>(null);
  const createDisplay = useCreateDisplay(sessionKey);

  const close = () => {
    createDisplay.reset();
    setName("");
    setKind("exam_room");
    setRoomId(null);
    setOpen(false);
  };

  const chooseKind = (next: DisplayKind) => {
    setKind(next);
    if (ROOM_FIELD[next] === "none") setRoomId(null);
  };

  const roomRequired = ROOM_FIELD[kind] === "required";
  const trimmedName = name.trim();
  const isValid = trimmedName !== "" && (!roomRequired || roomId !== null);

  const roomOptions = [
    ...(roomRequired ? [] : [{ value: NO_ROOM, label: "No room" }]),
    ...rooms
      .filter((room) => room.active)
      .map((room) => ({ value: room.id, label: room.name })),
  ];

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="admin-display-add"
      >
        Add display
      </Button>

      <Dialog
        open={open}
        onClose={close}
        size="sm"
        dismissable={!createDisplay.isPending}
        title="Add a display"
        footer={
          <>
            <Button
              variant="outline"
              onClick={close}
              disabled={createDisplay.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                createDisplay.mutate(
                  { name: trimmedName, kind, roomId },
                  {
                    onSuccess: (display) => {
                      close();
                      setAdded(display);
                    },
                  },
                )
              }
              disabled={!isValid}
              busy={createDisplay.isPending}
              data-testid="admin-display-add-submit"
            >
              {createDisplay.isPending ? "Adding" : "Add display"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            placeholder="e.g. Exam 2 wall"
            disabled={createDisplay.isPending}
            data-testid="admin-display-add-name"
          />
          <SegmentedControl
            label="Kind"
            aria-label="Kind"
            options={KIND_OPTIONS}
            value={kind}
            onValueChange={chooseKind}
            className="flex-wrap"
            data-testid="admin-display-add-kind"
          />
          {ROOM_FIELD[kind] !== "none" && (
            <Select
              label="Room"
              value={roomRequired ? roomId : (roomId ?? NO_ROOM)}
              onValueChange={(value) =>
                setRoomId(value === NO_ROOM ? null : value)
              }
              options={roomOptions}
              placeholder="Choose a room"
              disabled={createDisplay.isPending}
              data-testid="admin-display-add-room"
            />
          )}
          {createDisplay.isError && (
            <FieldError>
              {describeMutationError(createDisplay.error, "add the display")}
            </FieldError>
          )}
        </div>
      </Dialog>

      {added && (
        <PairDisplayDialog
          display={added}
          open
          onClose={() => setAdded(null)}
          sessionKey={sessionKey}
        />
      )}
    </>
  );
};
